'use client';

import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Stack,
  Text,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import CloudflareTurnstile from '@/app/_components/cloudflare-turnstile/CloudflareTurnstile';
import { DealersSelectNewCars } from '@/app/_components/modals/enquire-forms/enquire-now-form-new-cars/components/dealers-select/dealers-select';
import Styles from '@/app/_components/modals/enquire-forms/enquire-now-form-new-cars/components/enquire-form/enquire-form.module.scss';
import { LocationButtonNewCars } from '@/app/_components/modals/enquire-forms/enquire-now-form-new-cars/components/location-button/location-button';
import NewCarSelect from '@/app/_components/modals/enquire-forms/enquire-now-form-new-cars/components/newCar-select/newCar-select';
import { VariantsSelectNewCars } from '@/app/_components/modals/enquire-forms/enquire-now-form-new-cars/components/variant-select/variant-select';
import ThankYouMessage from '@/app/_components/modals/enquire-forms/newsletter-signup/newsletter/thank-you-message/thank-you-message';
import { PopiaNotice } from '@/app/_components/popia-notice/popia-notice';
import {
  ERROR_DESCRIPTION,
  ERROR_TITLE,
} from '@/constants/enquiry-forms/enquiry-forms.constants';
import {
  SPECIAL_FOR_SERVICE,
  SPECIAL_FOR_VEHICLE,
} from '@/constants/specials/specials.constants';
import {
  validateFormNameFiled,
  validateOptionalFormEmailField,
  validatePhoneNumberField,
} from '@/constants/validation.constants';
import { useDealersInfo } from '@/hooks/dealers/dealers.hook';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { InjectLeadXSourceCode } from '@/services/leadx.service';
import {
  getFloorCodeForSpecials,
  getSpecialSource,
} from '@/services/link.service';
import { getNewCarSpec } from '@/services/NewCarsService.service';
import { getLeadTypeForSpecial } from '@/services/specials/specials.service';
import { SpecialDetails } from '@/services/SpecialsService.service';
import { createLead } from '@/services/UsedCarsService.service';
import { DocumnetPaia_Popia } from '@/types/config/config.types';
import { NewCar, SpecificationsPricesType,
} from '@/types/new-cars/new-cars.types';
import { VehicleType } from '@/types/used-cars/used-cars.types';
import { getUtmData } from '@/utils/getUtmData';

import styles from './PromotionsForm.module.scss';

enum IsUsedVehicleLead {
  Yes = '1',
  No = '0',
}

export type PromotionsFormType = 'enquiry' | 'whatsapp';

interface FormValues {
  user_name: string;
  email: string;
  phone_number: string;
  message: string;
  branch_name: string | null;
  car_model: string | null;
  'cf-turnstile-response'?: string;
}

interface PromotionsFormProps {
  specialDetails: SpecialDetails;
  config?: DocumnetPaia_Popia;
  isOpen?: boolean;
  onClose?: () => void;
  formType?: PromotionsFormType;
  isGlobalDrawer?: boolean;
  newCars?: NewCar[];
}

const animateButton = (shouldAnimate: boolean = true) => {
  if (!shouldAnimate) return;

  setTimeout(() => {
    const primaryBtn = document.querySelector<HTMLElement>('.primaryBtn');

    if (!primaryBtn) return;

    primaryBtn.classList.add('pulse-animation');

    if (!primaryBtn.style.getPropertyValue('--original-bg')) {
      const computedStyle = getComputedStyle(primaryBtn);

      primaryBtn.style.setProperty(
        '--original-bg',
        computedStyle.backgroundColor,
      );
    }

    const removeAnimation = () => {
      primaryBtn.classList.remove('pulse-animation');
      primaryBtn.removeEventListener('animationend', removeAnimation);
    };

    primaryBtn.addEventListener('animationend', removeAnimation);
  }, 300);
};

function PromotionsForm({
  specialDetails,
  config,
  isOpen = true,
  onClose,
  formType = 'enquiry',
  isGlobalDrawer = false,
  newCars = [],
}: PromotionsFormProps) {
  const isDesktop = useBreakpointValue({
    base: false,
    lg: true,
  });

  const [loading, setIsLoading] = useState(false);
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [availableNewCars, setAvailableNewCars] = useState<NewCar[]>(newCars);
  const specialVariantMMCode = specialDetails?.mmcode || null;
  const specialRangeSlug = specialDetails?.range?.name?.toLowerCase().replace(/\s+/g, '-');
  const { closeLeadForm } = useFloatingCTA();

  const specialNewCarId = availableNewCars.find(
    (newCar) =>
      newCar?.specification_prices?.some(
        (spec) =>
          spec.mm_code === specialVariantMMCode,
      ) ||
      newCar.slug?.toLowerCase() === specialRangeSlug,
  )?.id;

  const [selectedNewCarId, setSelectedNewCarId] =
    useState<number | null>(() => {
      if (specialDetails?.model?.id) {
        const matchingCar = availableNewCars.find(
          (car) => car.id === specialDetails.model?.id,
        );

        if (matchingCar) {
          return matchingCar.id;
        }
      }

      return specialNewCarId || null;
    });

  const [selectedVariant, setSelectedVariant] = useState<SpecificationsPricesType | null>(null);
  const [isFetchingVariants, setIsFetchingVariants] = useState(false);

  const toast = useToast();
  const leadType = getLeadTypeForSpecial(specialDetails); 

  const availableDealershipIds =
    specialDetails?.dealerships?.map(
      (dealership) =>
        dealership.dealerships_id.id,
    );

  const selectedNewCar = availableNewCars.find(
    (car) => car.id === selectedNewCarId,
  );

  const disableSelectCar = formType === 'whatsapp' && Boolean(selectedNewCarId);

  const carVariants =
    selectedNewCar?.specification_prices || [];

  useEffect(() => {
    setAvailableNewCars(newCars || []);
  }, [newCars]);

  useEffect(() => {
    if (specialDetails?.model?.name) {
      return;
    }

    if (!availableNewCars.length || !specialNewCarId) {
      return;
    }

    setSelectedNewCarId(specialNewCarId);
  }, [
    availableNewCars,
    specialDetails?.model?.name,
    specialNewCarId,
  ]);

  useEffect(() => {
    const fetchVariants = async () => {
      if (!selectedNewCarId) {
        setSelectedVariant(null);
        return;
      }

      try {
        setIsFetchingVariants(true);

        const { data: fetchedCarSpec } =
          await getNewCarSpec(selectedNewCarId);

        const hasValidSpecifications =
          fetchedCarSpec?.specification_prices &&
          fetchedCarSpec.specification_prices.length > 0;

        if (!hasValidSpecifications) {
          return;
        }

        setAvailableNewCars((currentCars) =>
          currentCars.map((car) =>
            car.id === selectedNewCarId
              ? {
                  ...car,
                  specification_prices:
                    fetchedCarSpec.specification_prices,
                }
              : car,
          ),
        );

        if (specialVariantMMCode) {
          const matchingVariant =
            fetchedCarSpec.specification_prices.find(
              (variant) =>
                variant.mm_code ===
                specialVariantMMCode,
            );

          if (matchingVariant) {
            setSelectedVariant(matchingVariant);
          }
        }
      } catch (error) {
        console.error(
          'Failed to fetch WhatsApp model variants:',
          error,
        );
      } finally {
        setIsFetchingVariants(false);
      }
    };

    fetchVariants();
  }, [
    selectedNewCarId,
    specialVariantMMCode,
  ]);

  const form = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      user_name: '',
      email: '',
      phone_number: '',
      message: '',
      branch_name: null,
      car_model: selectedNewCarId
        ? selectedNewCarId.toString()
        : null,
      'cf-turnstile-response': undefined,
    },
  });

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = form;

  const { dealersInfo } = useDealersInfo();

  const onSubmit = async (values: FormValues) => {
    const utm_data = getUtmData();

    setIsLoading(true);

    const dealer = dealersInfo.find(({ id }) => id === values.branch_name);
    const special = specialDetails;
    const leadType = getLeadTypeForSpecial(specialDetails);

    const specialDealership = special.dealerships.length
      ? special.dealerships[0]?.dealerships_id
      : null;

    const dealerName = dealer?.id;

    const source = getSpecialSource(special);
    const leadSource =
      formType === 'whatsapp'
        ? `${source}-WhatsApp`
        : source;

    const fallbackVariant =
      selectedVariant ||
      carVariants.find(
        (variant) =>
          variant.mm_code === specialVariantMMCode,
      ) ||
      carVariants
        .slice()
        .sort(
          (a, b) =>
            parseFloat(a.price) - parseFloat(b.price),
        )[0] ||
      null;

    const vehicleModel =
      fallbackVariant?.title ||
      selectedNewCar?.slug ||
      special.model?.name ||
      '';

    const vehicleMMCode =
      fallbackVariant?.mm_code ||
      special.mmcode ||
      '';

    const leadData = {
      ...values,
      vehicles: null,
      lead_type: leadType,
      lead_channel: formType,
      dealer_id: dealer?.id || specialDealership?.id,
      dealer_name: dealer?.name || specialDealership?.name,
      dealer_city: dealer?.city.name || specialDealership?.city?.name,
      dealer_province:
        dealer?.city.name || specialDealership?.city?.province.name,
      dealer_country:
        dealer?.city?.country?.name || specialDealership?.city?.country?.name,
      vehicle_brand: special.brand?.name,
      vehicle_range: special.range?.name || '',
      vehicle_model: vehicleModel,
      vehicle_mmcode: vehicleMMCode,
      vehicle_kms: special?.mileage,
      vehicle_year: special?.year || null,
      vehicle_colour: special?.vehicles?.spec_body_colors?.name,
      vehicle_vin: special?.vehicles?.vin || '',
      vehicle_price: special?.vehicle_price || null,
      vehicle_registration: special?.vehicles?.registration_number || '',
      vehicle_drivetrain: special?.vehicles?.spec_drivetrain?.value || '',
      vehicle_transmission: special?.vehicles?.spec_transmission?.name || '',
      vehicle_fuel: special?.vehicles?.spec_fuel_types?.name || '',
      vehicle_doors: special?.vehicles?.spec_doors.name || '',
      vehicle_image: special?.vehicles?.main_image || '',
      vehicle_images: special?.vehicles?.images || '',
      vehicle_title: special?.title,
      vehicle_body_type: special?.vehicles?.spec_body_type.name || '',
      vehicle_type: special?.vehicle_type,
      dealership_id: dealer?.id || specialDealership?.id,
      dealership_name: dealer?.name || specialDealership?.name,
      contact_firstname: values.user_name,
      contact_surname: values.user_name,
      contact_email: values.email,
      contact_officephone: values.phone_number,
      contact_cellphone: values.phone_number,
      comments: values.message,
      special: 'special',
      dealer_ref:
        dealer?.cms_used_dealer_code ||
        dealer?.cms_new_dealer_code ||
        specialDealership?.cms_used_dealer_code ||
        specialDealership?.cms_new_dealer_code,
      dealer_floor:
        special?.special_for === SPECIAL_FOR_VEHICLE || !special?.special_for
          ? getFloorCodeForSpecials(
              dealer?.cms_used_leads ?? specialDealership?.cms_used_leads,
              dealer?.cms_new_leads ?? specialDealership?.cms_new_leads,
              special?.vehicle_type,
            )
          : special?.special_for === SPECIAL_FOR_SERVICE
            ? dealer?.cms_service_leads
            : dealer?.cms_parts_leads,
      vehicle_used:
        special?.vehicle_type === VehicleType.New
          ? IsUsedVehicleLead.No
          : IsUsedVehicleLead.Yes,
      related_specials: special?.id || null,
      source: leadSource,
      utm_source: utm_data?.utm_source || '',
      utm_medium: utm_data?.utm_medium || '',
      utm_campaign: utm_data?.utm_campaign || '',
      utm_content: utm_data?.utm_content || '',
      utm_term: utm_data?.utm_term || '',
      vehicle_testdrive_location_at_contact: false,
      vehicle_testdrive: leadType === 'test-drive',
      utm_dealership: utm_data?.utm_dealership || '',
      utm_salesperson: utm_data?.utm_salesperson || '',
      dealerSalesPerson: utm_data?.utm_salesperson || '',
      dealer_salesperson: utm_data?.utm_salesperson || '',
      fbclid: utm_data?.fbclid || '',
      gclid: utm_data?.gclid || '',
      dealership_micro_site: dealerName,
    };

    try {
      const enrichedLeadData =
        await InjectLeadXSourceCode(
          leadData,
        );

      await createLead(enrichedLeadData);
      if (formType === 'whatsapp') {
        // TODO:
        // Call RED WhatsApp API here.
        // Log the API response in whatsapp_messages_log.
      }

      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      toast({
        title: ERROR_TITLE,
        description: ERROR_DESCRIPTION,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    animateButton(isOpen);
  };

  return (
    <Box
      className={cn(
        styles.stickyForm,
        isGlobalDrawer &&
          styles.globalDrawerForm,
      )}
    >
      {isDesktop && isOpen && !isGlobalDrawer && (
        <IconButton
          aria-label="Close form"
          icon={<CloseIcon />}
          onClick={handleClose}
          className={styles.closeButton}
          size="sm"
        />
      )}
      {(isDesktop && isOpen) || !isDesktop ? (
        <Box
          gap={2}
          className={cn(
            styles.formContainer,
            isGlobalDrawer &&
              styles.globalDrawerFormContainer,
          )}
        >
          {isSubmitted ? (
            <ThankYouMessage 
              onDone={() => {
                onClose?.();
                setIsSubmitted(false);
                closeLeadForm();
              }} 
            />
          ) : (
            <Stack
              as="form"
              onSubmit={handleSubmit(onSubmit)}
              className={cn(Styles.enquireNowForm, Styles.enquireFormWrapper)}
              gap={4}
              name="PromotionsForm"
              id="promotionsForm"
            >
              <Text fontSize={28} fontWeight="bold">
                {formType === 'whatsapp'
                  ? 'WhatsApp me back!'
                  : 'Enquire Today!'}
              </Text>
              {formType === 'whatsapp' && (
                <>
                    <Box>
                      <Text fontSize={16}>
                        I am interested in
                      </Text>
                    </Box>
                    {availableNewCars.length > 0 && (
                      <Stack
                        gap={4}
                        mb={-2}
                      >
                        <FormControl
                          id="car_model"
                          isInvalid={
                            !!errors.car_model
                          }
                        >
                          <NewCarSelect
                            newCars={availableNewCars}
                            name="car_model"
                            selectedNewCarId={selectedNewCarId}
                            setSelectedNewCarId={(id) => {
                              setSelectedNewCarId(id);
                              setSelectedVariant(null);
                            }}
                            register={register}
                            required={false}
                            disabled={disableSelectCar}
                          />

                          <FormErrorMessage>
                            {errors.car_model
                              ?.message}
                          </FormErrorMessage>
                        </FormControl>

                        <VariantsSelectNewCars
                          variants={carVariants}
                          selectedVariant={
                            selectedVariant
                          }
                          setSelectedVariant={(
                            variant,
                          ) => {
                            setSelectedVariant(
                              variant,
                            );
                          }}
                          disabled={
                            isFetchingVariants ||
                            !selectedNewCarId
                          }
                          leadType={leadType}
                        />
                      </Stack>
                    )}
                </>
              )}

              <FormControl
                id="user_name"
                isInvalid={
                  !!errors.user_name
                }
              >
                <Box
                  className={
                    styles.floatingForm
                  }
                >
                  <Input
                    {...register(
                      'user_name',
                      {
                        validate:
                          validateFormNameFiled,
                      },
                    )}
                    name="user_name"
                    className={
                      styles.formInput
                    }
                    variant="flushed"
                    placeholder=" "
                  />

                  <FormLabel
                    className={
                      styles.formLabel
                    }
                  >
                    Name*
                  </FormLabel>
                </Box>

                <FormErrorMessage>
                  {errors.user_name?.message}
                </FormErrorMessage>
              </FormControl>

              {formType === 'enquiry' && (
                <FormControl
                  id="email"
                  isInvalid={
                    !!errors.email
                  }
                >
                  <Box
                    className={
                      styles.floatingForm
                    }
                  >
                    <Input
                      {...register(
                        'email',
                        {
                          validate:
                            validateOptionalFormEmailField,
                        },
                      )}
                      name="email"
                      className={
                        styles.formInput
                      }
                      variant="flushed"
                      placeholder=" "
                      type="email"
                    />

                    <FormLabel
                      className={
                        styles.formLabel
                      }
                    >
                      E-mail
                    </FormLabel>
                  </Box>

                  <FormErrorMessage>
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>
              )}

              <FormControl
                id="phone_number"
                isInvalid={
                  !!errors.phone_number
                }
              >
                <Box
                  className={
                    styles.floatingForm
                  }
                >
                  <Input
                    {...register(
                      'phone_number',
                      {
                        required:
                          'Please input your Phone number!',
                        validate:
                          validatePhoneNumberField,
                      },
                    )}
                    name="phone_number"
                    className={
                      styles.formInput
                    }
                    variant="flushed"
                    placeholder=" "
                    type="tel"
                  />

                  <FormLabel
                    className={
                      styles.formLabel
                    }
                  >
                    {formType === 'whatsapp'
                      ? 'WhatsApp Number*'
                      : 'Mobile Number*'}
                  </FormLabel>
                </Box>

                <FormErrorMessage>
                  {
                    errors.phone_number
                      ?.message
                  }
                </FormErrorMessage>
              </FormControl>

              <LocationButtonNewCars
                availableDealershipIds={
                  availableDealershipIds
                }
              />

              <FormControl
                id="branch_name"
                isInvalid={
                  !!errors.branch_name
                }
              >
                <DealersSelectNewCars
                  name="branch_name"
                  register={register}
                  onChange={(val) =>
                    setValue(
                      'branch_name',
                      val,
                    )
                  }
                  required
                  availableDealershipIds={
                    availableDealershipIds
                  }
                />

                <FormErrorMessage>
                  {
                    errors.branch_name
                      ?.message
                  }
                </FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                isDisabled={
                  isTurnstileLoading
                }
                isLoading={loading}
                loadingText={
                  formType === 'whatsapp'
                    ? 'Opening WhatsApp...'
                    : 'Submitting'
                }
                data-cy={
                  formType === 'whatsapp'
                    ? 'send-whatsapp-button'
                    : 'send-enquiry-button'
                }
                className={cn(
                  styles.btn,
                  'primaryBtn',
                  formType === 'whatsapp' &&
                    styles.whatsappSubmitButton,
                )}
                width="auto"
              >
                {formType === 'whatsapp' && (
                  <img
                    src="/icons/whatsapp.svg"
                    alt=""
                    width={30}
                    height={30}
                  />
                )}

                <span>
                  {formType === 'whatsapp'
                    ? 'WhatsApp Me'
                    : 'Call me back!'}
                </span>
              </Button>

              <CloudflareTurnstile
                onVerify={(token) => {
                  setValue('cf-turnstile-response', token);
                  setIsTurnstileLoading(false);
                }}
                onError={() => setIsTurnstileLoading(false)}
                onLoad={() => setIsTurnstileLoading(true)}
              />

              {config?.popia_notice && (
                <PopiaNotice
                  documentPaiaPopia={{
                    popia_notice: config.popia_notice,
                  }}
                />
              )}
            </Stack>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

export default PromotionsForm;
